import { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, NavLink } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  LayoutDashboard, Users, CreditCard, UserCircle, Calendar, 
  Wallet, Clock, FileText, Settings, LogOut, Menu, X, 
  TrendingUp, TrendingDown, AlertCircle, CheckCircle,
  Building2, Receipt, Shield, ChevronDown, Layers
} from "lucide-react";

// Pages
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import SedaFinanceNew from "./pages/SedaFinanceNew";
import CustomerDetail from "./pages/CustomerDetail";
import Payments from "./pages/Payments";
import Employees from "./pages/Employees";
import Leaves from "./pages/Leaves";
import Advances from "./pages/Advances";
import Salaries from "./pages/Salaries";
import Attendance from "./pages/Attendance";
import Taxes from "./pages/Taxes";
import Insurance from "./pages/Insurance";
import Bagkur from "./pages/Bagkur";
import Expenses from "./pages/Expenses";
import FiscalCalendar from "./pages/FiscalCalendar";
import Alerts from "./pages/Alerts";
import Documents from "./pages/Documents";
import BankAccounts from "./pages/BankAccounts";
import Installments from "./pages/Installments";
import SettingsPage from "./pages/Settings";
import BelekKiralama from "./pages/BelekKiralama";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const LoginPage = () => {
  const navigate = useNavigate();
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email.trim(),
        password: password
      }, { withCredentials: true });
      
      toast.success(`Hoş geldiniz, ${response.data.name}!`);
      navigate('/dashboard', { state: { user: response.data } });
    } catch (error) {
      const message = error.response?.data?.detail || "Giriş başarısız";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex" data-testid="login-page">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1561212001-d400282c6144?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjB0ZXh0dXJlfGVufDB8fHxibGFja3wxNzc0MjU2OTA1fDA&ixlib=rb-4.1.0&q=85"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-transparent to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <h1 className="text-4xl font-bold text-zinc-50 font-outfit">Ajans Finans</h1>
          <p className="text-zinc-400 mt-2 text-lg">Muhasebe & İK Takip Sistemi</p>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center lg:text-left">
            <h2 className="text-3xl font-bold text-zinc-50 font-outfit">Hoş Geldiniz</h2>
            <p className="text-zinc-400 mt-2">Devam etmek için giriş yapın</p>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-8">
            {!showEmailLogin ? (
              <>
                <button
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                  className="w-full flex items-center justify-center gap-3 bg-zinc-950 hover:bg-zinc-800 border border-zinc-700 text-zinc-50 py-3 px-4 rounded-sm transition-colors duration-200"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google ile Giriş Yap
                </button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-zinc-700"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-zinc-900 text-zinc-500">veya</span>
                  </div>
                </div>

                <button
                  onClick={() => setShowEmailLogin(true)}
                  data-testid="show-email-login-btn"
                  className="w-full flex items-center justify-center gap-2 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 text-amber-400 py-3 px-4 rounded-sm transition-colors duration-200"
                >
                  <UserCircle className="w-5 h-5" />
                  Email ile Giriş Yap
                </button>
              </>
            ) : (
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="email-input"
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-sm px-4 py-2.5 text-zinc-50 placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
                    placeholder="email@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Şifre</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="password-input"
                    className="w-full bg-zinc-950 border border-zinc-700 rounded-sm px-4 py-2.5 text-zinc-50 placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
                    placeholder="••••••••"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="email-login-submit-btn"
                  className="w-full bg-amber-400 hover:bg-amber-500 text-zinc-950 font-medium py-3 px-4 rounded-sm transition-colors duration-200 disabled:opacity-50"
                >
                  {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEmailLogin(false)}
                  className="w-full text-zinc-400 hover:text-zinc-50 text-sm py-2 transition-colors"
                >
                  ← Google ile giriş yap
                </button>
              </form>
            )}

            <div className="mt-6 p-4 bg-amber-400/10 border border-amber-400/20 rounded-sm">
              <p className="text-amber-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Sadece yetkili hesap ile giriş yapılabilir
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error("Giriş başarısız");
        navigate('/login');
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.get(`${API}/auth/session`, {
          params: { session_id: sessionId },
          withCredentials: true
        });

        toast.success(`Hoş geldiniz, ${response.data.name}!`);
        navigate('/dashboard', { state: { user: response.data } });
      } catch (error) {
        const message = error.response?.data?.detail || "Giriş başarısız";
        toast.error(message);
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-zinc-400 mt-4">Giriş yapılıyor...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Skip if user data passed from AuthCallback
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Sidebar Component
const Sidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success("Çıkış yapıldı");
      navigate('/login');
    } catch (error) {
      toast.error("Çıkış yapılamadı");
    }
  };

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    { icon: Wallet, label: "SEDA FİNANS", path: "/seda-finance", highlight: true },
    { icon: AlertCircle, label: "Uyarılar", path: "/alerts" },
    { icon: Building2, label: "Banka Hesapları", path: "/bank-accounts" },
    { icon: Layers, label: "Taksitler", path: "/installments" },
    { icon: Users, label: "Müşteriler", path: "/customers" },
    { icon: CreditCard, label: "Ödemeler", path: "/payments" },
    { icon: Receipt, label: "Giderler", path: "/expenses" },
    { icon: FileText, label: "Belgeler", path: "/documents" },
    { icon: UserCircle, label: "Personel", path: "/employees" },
    { icon: Calendar, label: "İzinler", path: "/leaves" },
    { icon: Wallet, label: "Avanslar", path: "/advances" },
    { icon: Receipt, label: "Maaşlar", path: "/salaries" },
    { icon: Clock, label: "Puantaj", path: "/attendance" },
    { icon: Building2, label: "Vergi", path: "/taxes" },
    { icon: Shield, label: "SGK/Sigorta", path: "/insurance" },
    { icon: Shield, label: "Bağkur", path: "/bagkur" },
    { icon: Calendar, label: "Mali Takvim", path: "/fiscal-calendar" },
    { icon: Building2, label: "Belek Kiralama", path: "/belek-kiralama", highlight: true },
    { icon: Settings, label: "Ayarlar", path: "/settings" },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-zinc-900 border-r border-zinc-800 z-50 transform transition-transform duration-200 lg:translate-x-0 flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-400 rounded-sm flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-zinc-950" />
            </div>
            <span className="font-outfit font-semibold text-zinc-50">Ajans Finans</span>
          </div>
          <button 
            onClick={onClose}
            className="lg:hidden text-zinc-400 hover:text-zinc-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu - scrollable */}
        <nav className="p-4 space-y-1 overflow-y-auto flex-1">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-200 ${
                  isActive 
                    ? 'bg-amber-400/10 text-amber-400 border-l-2 border-amber-400 -ml-px pl-[11px]' 
                    : item.highlight 
                      ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-400/10 font-medium' 
                      : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800'
                }`
              }
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User - fixed at bottom */}
        <div className="p-4 border-t border-zinc-800 flex-shrink-0">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                <UserCircle className="w-6 h-6 text-zinc-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-50 truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-rose-400 hover:bg-rose-400/10 rounded-sm transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" />
            Çıkış Yap
          </button>
        </div>
      </aside>
    </>
  );
};

// Layout Component
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-zinc-950 noise-bg">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Main content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="h-16 bg-zinc-900 border-b border-zinc-800 flex items-center justify-between px-4 lg:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-zinc-400 hover:text-zinc-50"
            data-testid="menu-toggle"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1" />
          <div className="text-sm text-zinc-500">
            {new Date().toLocaleDateString('tr-TR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </header>

        {/* Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - must be synchronous
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/seda-finance" element={
        <ProtectedRoute>
          <Layout><SedaFinanceNew /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute>
          <Layout><Customers /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/customers/:id" element={
        <ProtectedRoute>
          <Layout><CustomerDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/payments" element={
        <ProtectedRoute>
          <Layout><Payments /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/employees" element={
        <ProtectedRoute>
          <Layout><Employees /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/leaves" element={
        <ProtectedRoute>
          <Layout><Leaves /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/advances" element={
        <ProtectedRoute>
          <Layout><Advances /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/salaries" element={
        <ProtectedRoute>
          <Layout><Salaries /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/attendance" element={
        <ProtectedRoute>
          <Layout><Attendance /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/taxes" element={
        <ProtectedRoute>
          <Layout><Taxes /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/insurance" element={
        <ProtectedRoute>
          <Layout><Insurance /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/bagkur" element={
        <ProtectedRoute>
          <Layout><Bagkur /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/expenses" element={
        <ProtectedRoute>
          <Layout><Expenses /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/fiscal-calendar" element={
        <ProtectedRoute>
          <Layout><FiscalCalendar /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/alerts" element={
        <ProtectedRoute>
          <Layout><Alerts /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <Layout><Documents /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/bank-accounts" element={
        <ProtectedRoute>
          <Layout><BankAccounts /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/installments" element={
        <ProtectedRoute>
          <Layout><Installments /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/belek-kiralama" element={
        <ProtectedRoute>
          <Layout><BelekKiralama /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout><SettingsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster 
        position="top-right" 
        theme="dark"
        toastOptions={{
          style: {
            background: '#18181b',
            border: '1px solid #27272a',
            color: '#fafafa',
          },
        }}
      />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
export { API, BACKEND_URL };
